import React, { useState } from 'react';
import { ptsFor } from '../utils/points';

// Retiring a chore archives it by default — it leaves the active list but its
// full log/grade history and every point it earned stay on the scoreboard,
// and it can be restored later. Permanently deleting it is a deliberate
// second step that shows exactly what the scoreboard loses first.
const ArchiveChoreSheet = ({ chore, onArchive, onPurge, onClose }) => {
  const [showPurge, setShowPurge] = useState(false);

  const logs = chore?.logs || [];
  const totalPoints = logs.reduce((sum, l) => sum + ptsFor(l.actualMinutes, l.actualDifficulty), 0);

  const byPerson = new Map();
  logs.forEach(log => {
    (log.user || []).forEach(u => {
      if (!byPerson.has(u.id)) byPerson.set(u.id, { ...u, points: 0 });
      byPerson.get(u.id).points += ptsFor(log.actualMinutes, log.actualDifficulty);
    });
  });
  const impact = Array.from(byPerson.values()).sort((a, b) => b.points - a.points);

  return (
    <div className="fixed inset-0 bg-ink-overlay/70 backdrop-blur-sm z-[60] flex items-end justify-center animate-in fade-in duration-200" onClick={onClose}>
      <div
        className="bg-ink-card2 w-full max-w-md rounded-t-3xl border-t border-white/10 p-6 shadow-2xl animate-in slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-5" />

        {!showPurge ? (
          <>
            <h2 className="text-xl font-serif text-ink-fg">Archive "{chore.title}"?</h2>
            <p className="text-sm text-ink-fg/60 mt-3">
              It leaves the list, but {logs.length > 0 ? `all ${logs.length} past log${logs.length === 1 ? '' : 's'}, their reviews, and every point stay` : 'its history stays'} on the scoreboard. You can bring it back any time.
            </p>

            <div className="mt-4 p-3.5 rounded-2xl bg-mint/10 border border-mint/20">
              <p className="text-sm font-semibold text-ink-fg">Scoreboard unchanged</p>
              {impact.length > 0 && (
                <p className="text-xs text-ink-fg/55 mt-1.5">
                  {impact.map(p => `${p.name} ${p.points}`).join(' · ')}
                </p>
              )}
            </div>

            {logs.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPurge(true)}
                className="w-full mt-4 h-9 text-[11.5px] font-semibold text-danger hover:text-danger/80 transition-colors"
              >
                Delete permanently instead
              </button>
            )}

            <div className="flex gap-2.5 mt-5">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-12 rounded-2xl border border-white/15 text-ink-fg font-semibold text-sm hover:bg-ink-card3 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onArchive(chore.id)}
                className="flex-1 h-12 rounded-2xl bg-mint text-ink-shell font-bold text-sm hover:brightness-95 transition-all active:scale-95"
              >
                Archive
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-serif text-ink-fg">Delete "{chore.title}" permanently?</h2>
            <p className="text-sm text-ink-fg/60 mt-3">
              This removes all {logs.length} log{logs.length === 1 ? '' : 's'} and their reviews for good — it cannot be undone, and these points come off the scoreboard.
            </p>

            <div className="mt-4 p-3.5 rounded-2xl bg-danger/10 border border-danger-border/25">
              <p className="text-[10px] font-mono font-medium text-danger uppercase tracking-widest">Scoreboard after deleting</p>
              <div className="flex flex-col gap-2 mt-3">
                {impact.map(p => (
                  <div key={p.id} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color || 'oklch(0.72 0.155 48)' }} />
                    <span className="flex-1 text-sm font-semibold text-ink-fg/90">{p.name}</span>
                    <span className="text-xs font-mono text-ink-fg/40 line-through">{p.points}</span>
                    <span className="text-sm font-mono font-bold text-danger min-w-[2.5rem] text-right">−{p.points}</span>
                  </div>
                ))}
                {impact.length === 0 && (
                  <p className="text-xs text-ink-fg/40 italic">No points on record for this chore.</p>
                )}
              </div>
              <p className="text-xs text-ink-fg/40 mt-3">{totalPoints} points total across all roommates.</p>
            </div>

            <div className="flex gap-2.5 mt-5">
              <button
                type="button"
                onClick={() => setShowPurge(false)}
                className="flex-none w-24 h-12 rounded-2xl border border-white/15 text-ink-fg/70 font-semibold text-sm hover:bg-ink-card3 transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => onPurge(chore.id)}
                className="flex-1 h-12 rounded-2xl bg-danger text-ink-shell font-bold text-sm hover:brightness-95 transition-all active:scale-95"
              >
                Delete Permanently
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ArchiveChoreSheet;
